import { UTCDate } from '@date-fns/utc';
import { BeneficiaryType } from '@seapoint/server/enums';
import { createColumnHelper } from '@tanstack/react-table';
import { format, isSameYear } from 'date-fns';
import { AvatarNameDescription } from '../../../design-system/avatar/avatar-name-description/avatar-name-description';
import { Button } from '../../../design-system/button/button';
import { BeneficiaryView } from '../../../types/entity-types';
import { getAmountWithCurrency } from '../../../utils/get-amount-with-currency';
import { getFormattedSortCode } from '../../../utils/get-formatted-sort-code';
import { getAccountDetails } from '../utils/get-account-details';
import { getLatestDebitTransferDate } from '../utils/get-latest-debit-transfer-date';
import { getTotalDebitTransferAmount } from '../utils/get-total-debit-transfer-amount';
import { isSupportedCurrency } from '../utils/is-supported-currency';

const columnHelper = createColumnHelper<BeneficiaryView>();

export const beneficiaryTableColumnsDefinition = [
  columnHelper.accessor('name', {
    header: 'Recipient',
    cell: ({ row }) => <AvatarNameDescription avatar={{ showAvatar: true }} name={row.original.name} />,
    size: 30,
  }),
  columnHelper.accessor(
    (row) => `${row.destination.iban} ${row.destination.accountNumber} ${row.destination.sortCode}`,
    {
      header: 'Bank Account',
      cell: ({ row }) => {
        const { currency, iban, accountNumber, sortCode } = {
          currency: row.original.destination.currency,
          iban: row.original.destination.iban,
          accountNumber: row.original.destination.accountNumber,
          sortCode: row.original.destination.sortCode,
        };

        if (!isSupportedCurrency(currency)) {
          return null;
        }

        const accountDetails = getAccountDetails({
          type: row.original.destination.type,
          accountNumber,
          sortCode,
          iban,
        });
        const formattedSortCode = getFormattedSortCode(accountDetails.sortCode);

        const avatarName =
          accountDetails.beneficiaryType === BeneficiaryType.IBAN
            ? accountDetails.iban
            : `${accountDetails.accountNumber} | ${formattedSortCode}`;

        return <AvatarNameDescription avatar={{ showAvatar: false }} name={avatarName} description={currency} />;
      },
      size: 30,
    },
  ),
  columnHelper.accessor(
    (row) =>
      getTotalDebitTransferAmount({
        transactions: row.transactions,
      }),
    {
      id: 'totalPaid',
      header: () => <p className="text-right">Total Paid</p>,
      cell: ({ row }) => {
        const totalAmount = getTotalDebitTransferAmount({
          transactions: row.original.transactions,
        });

        const amount = getAmountWithCurrency({
          amount: totalAmount,
          currency: row.original.destination.currency,
          isAccountBalance: true,
        });

        return <p className="text-right text-sm text-black">{amount}</p>;
      },
      size: 20,
    },
  ),
  columnHelper.accessor(
    (row) =>
      getLatestDebitTransferDate({
        transactions: row.transactions,
      }),
    {
      id: 'lastPaid',
      header: 'Last Paid',
      cell: ({ row }) => {
        const latestDebitTransferDate = getLatestDebitTransferDate({
          transactions: row.original.transactions,
        });

        if (!latestDebitTransferDate) {
          return null;
        }

        const dateFormat = isSameYear(new UTCDate(), latestDebitTransferDate) ? 'dd MMM' : 'dd MMM yyyy';

        return <p className="text-sm text-black">{format(latestDebitTransferDate, dateFormat)}</p>;
      },
      sortUndefined: -1,
      sortingFn: 'datetime',
      filterFn: (row, columnId, filterValue) => row.getValue(columnId) !== filterValue,
      size: 10,
      minSize: 10,
    },
  ),
  columnHelper.display({
    id: 'pay',
    cell: ({ table, row }) => {
      return (
        <Button
          variant="default"
          onClick={() => {
            table.options.meta?.onInitiateBeneficiaryPayment?.(row.original.id);
          }}
        >
          Pay
        </Button>
      );
    },
    size: 10,
    minSize: 10,
  }),
];
